import { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import Spacing from "@gx/dls/containers/Spacing";
import Icon, { IconVariant } from "@gx/dls/icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "@gx/dls/text/Text";

export enum EmptyLayoutSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

interface EmptyLayoutProps {
  size?: EmptyLayoutSize;
  title: string;
  description?: string;
  icon?: PhosphorIcon;
  svg?: React.ReactNode;
  actions?: React.ReactNode;
  fillHeight?: boolean;
}

const EmptyLayout = ({
  size = EmptyLayoutSize.MEDIUM,
  title,
  description,
  icon,
  svg,
  actions,
  fillHeight = true,
}: EmptyLayoutProps) => {
  return match(size)
    .with(EmptyLayoutSize.SMALL, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        fillWidth
        fillHeight={fillHeight}
      >
        {icon && (
          <Spacing bottom={8}>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={16} />
          </Spacing>
        )}
        {svg && (
          <Spacing bottom={8}>
            <FlexWrapper width={200}>{svg}</FlexWrapper>
          </Spacing>
        )}
        <Text weight={TextWeight.MEDIUM}>{title}</Text>
        <Spacing top={6} />
        <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_SM}>
          {description}
        </Text>
        <Spacing top={16} />
        {actions}
      </FlexWrapper>
    ))
    .with(EmptyLayoutSize.MEDIUM, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        overflow="hidden"
        fillWidth
        fillHeight={fillHeight}
      >
        {icon && (
          <Spacing bottom={12}>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={20} />
          </Spacing>
        )}
        {svg && <Spacing bottom={12}>{svg}</Spacing>}
        <Text size={TextSize.BODY_LG}>{title}</Text>
        <Spacing top={8} />
        <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_MD}>
          {description}
        </Text>
        <Spacing top={16} />
        {actions}
      </FlexWrapper>
    ))
    .with(EmptyLayoutSize.LARGE, () => (
      <FlexWrapper
        direction={FlexDirection.COLUMN}
        alignItems={AlignItems.CENTER}
        justifyContent={JustifyContent.CENTER}
        fillWidth
        fillHeight={fillHeight}
      >
        {icon && (
          <Spacing bottom={12}>
            <Icon component={icon} variant={IconVariant.TERTIARY} size={24} />
          </Spacing>
        )}
        {svg && <FlexWrapper width={360}>{svg}</FlexWrapper>}
        <Text size={TextSize.HEADING_MD}>{title}</Text>
        <Spacing top={16} />
        <Text variant={TextVariant.SECONDARY} size={TextSize.BODY_LG}>
          {description}
        </Text>
        <Spacing top={16} />
        {actions}
      </FlexWrapper>
    ))
    .exhaustive();
};

export default EmptyLayout;
